import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ArrowLeft, Save, Download, Search } from "lucide-react";
import { toast } from "sonner";
import { ClientPicker } from "@/components/ClientPicker";

export const Route = createFileRoute("/tools/warranty")({
  head: () => ({ meta: [{ title: "Warranty Tracker" }, { name: "robots", content: "noindex" }] }),
  component: WarrantyPage,
});

type W = {
  id: string; client_id?: string | null; client_name: string | null; client_address: string | null;
  product_name: string; brand: string | null; model: string | null; serial_number: string | null;
  install_date: string | null; warranty_months: number; expires_on: string | null;
  notes: string | null; purchase_price: number | null;
};
const empty = (): Partial<W> => ({ product_name: "", warranty_months: 12 });

function addMonths(d: string, m: number) {
  const dt = new Date(d + "T00:00:00");
  dt.setMonth(dt.getMonth() + m);
  return dt.toISOString().slice(0, 10);
}

function WarrantyPage() {
  const [list, setList] = useState<W[]>([]);
  const [current, setCurrent] = useState<Partial<W> | null>(null);
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await supabase.from("warranty_items").select("*").order("created_at", { ascending: false });
    setList((data as W[]) || []);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (current?.install_date && current.warranty_months && !current.expires_on) {
      setCurrent({ ...current, expires_on: addMonths(current.install_date, current.warranty_months) });
    }
  }, [current?.install_date, current?.warranty_months]);

  const save = async () => {
    if (!current?.product_name) return toast.error("Product name required");
    const payload = { ...current };
    if (payload.install_date && payload.warranty_months) payload.expires_on = addMonths(payload.install_date, payload.warranty_months);
    if (current.id) {
      const { error } = await supabase.from("warranty_items").update(payload).eq("id", current.id);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase.from("warranty_items").insert(payload as W);
      if (error) return toast.error(error.message);
    }
    toast.success("Saved"); setCurrent(null); load();
  };

  const del = async (id: string) => {
    if (!confirm("Delete?")) return;
    await supabase.from("warranty_items").delete().eq("id", id);
    load();
  };

  const filtered = useMemo(() => {
    if (!q) return list;
    const s = q.toLowerCase();
    return list.filter(w => [w.product_name, w.brand, w.model, w.serial_number, w.client_name].some(v => v?.toLowerCase().includes(s)));
  }, [list, q]);

  const exportCsv = () => {
    const rows = [["Client", "Address", "Product", "Brand", "Model", "Serial", "Install", "Expires", "Price"]];
    filtered.forEach(w => rows.push([w.client_name || "", w.client_address || "", w.product_name, w.brand || "", w.model || "", w.serial_number || "", w.install_date || "", w.expires_on || "", String(w.purchase_price || "")]));
    const csv = rows.map(r => r.map(c => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "warranty.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const daysLeft = (d: string | null) => d ? Math.floor((new Date(d).getTime() - Date.now()) / 86400000) : null;

  if (current) {
    return (
      <div className="max-w-2xl">
        <div className="mb-4 flex justify-between">
          <Button variant="ghost" size="sm" onClick={() => setCurrent(null)}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <Button size="sm" onClick={save}><Save className="h-4 w-4 mr-1" /> Save</Button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="sm:col-span-2"><ClientPicker clientId={current.client_id} clientName={current.client_name} onPick={(f) => setCurrent({ ...current, client_id: f.client_id, client_name: f.client_name, client_address: f.client_address })} /></div>
            <F label="Client name"><Input value={current.client_name || ""} onChange={e => setCurrent({ ...current, client_name: e.target.value })} /></F>
            <F label="Client address"><Input value={current.client_address || ""} onChange={e => setCurrent({ ...current, client_address: e.target.value })} /></F>
          </div>
          <F label="Product name *"><Input value={current.product_name || ""} onChange={e => setCurrent({ ...current, product_name: e.target.value })} /></F>
          <div className="grid grid-cols-3 gap-3">
            <F label="Brand"><Input value={current.brand || ""} onChange={e => setCurrent({ ...current, brand: e.target.value })} /></F>
            <F label="Model"><Input value={current.model || ""} onChange={e => setCurrent({ ...current, model: e.target.value })} /></F>
            <F label="Serial #"><Input value={current.serial_number || ""} onChange={e => setCurrent({ ...current, serial_number: e.target.value })} /></F>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label="Install date"><Input type="date" value={current.install_date || ""} onChange={e => setCurrent({ ...current, install_date: e.target.value })} /></F>
            <F label="Warranty (months)"><NumberInput value={current.warranty_months || 12} onValueChange={n => setCurrent({ ...current, warranty_months: n })} /></F>
            <F label="Purchase price"><NumberInput value={current.purchase_price || 0} onValueChange={n => setCurrent({ ...current, purchase_price: n })} /></F>
          </div>
          <F label="Notes"><Textarea rows={3} value={current.notes || ""} onChange={e => setCurrent({ ...current, notes: e.target.value })} /></F>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Warranty & Serial Tracker</h1>
          <p className="text-sm text-muted-foreground mt-1">Track installed products and warranty expiration.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv}><Download className="h-4 w-4 mr-1" /> CSV</Button>
          <Button onClick={() => setCurrent(empty())}><Plus className="h-4 w-4 mr-1" /> New</Button>
        </div>
      </div>
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Search product, brand, serial, client…" value={q} onChange={e => setQ(e.target.value)} />
      </div>
      <div className="rounded-lg border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr><th className="text-left p-2">Product</th><th className="text-left p-2">Client</th><th className="text-left p-2">Serial</th><th className="text-left p-2">Installed</th><th className="text-left p-2">Expires</th><th></th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map(w => {
              const dl = daysLeft(w.expires_on);
              return (
                <tr key={w.id} className="hover:bg-accent cursor-pointer" onClick={() => setCurrent(w)}>
                  <td className="p-2"><div className="font-medium">{w.product_name}</div><div className="text-xs text-muted-foreground">{w.brand} {w.model}</div></td>
                  <td className="p-2">{w.client_name}</td>
                  <td className="p-2 font-mono text-xs">{w.serial_number}</td>
                  <td className="p-2">{w.install_date}</td>
                  <td className="p-2">
                    {w.expires_on}
                    {dl !== null && (
                      <span className={`ml-2 text-xs ${dl < 0 ? "text-red-400" : dl < 60 ? "text-yellow-400" : "text-muted-foreground"}`}>
                        {dl < 0 ? `Expired ${-dl}d ago` : `${dl}d left`}
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-right"><Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); del(w.id); }}><Trash2 className="h-4 w-4" /></Button></td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No items.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function F({ label, children }: { label: string; children: React.ReactNode }) {
  return (<label className="block"><div className="text-xs font-medium text-muted-foreground mb-1">{label}</div>{children}</label>);
}
